/**
 * Subscription tier helpers — METALARK-1015
 *
 * The canonical tiers are the three in SubscriptionTier
 * (api/constants/retention-config.ts): standard, professional, enterprise.
 *
 * Stored values do not match that cleanly, for two reasons:
 *
 *  1. Casing. Backend write paths store lowercase, but the admin create form
 *     wrote uppercase for years, so production holds both ("standard" next to
 *     "ENTERPRISE"). Filtering on one casing silently drops rows written the
 *     other way.
 *  2. `basic` is the OLD NAME for `standard` — see the note in
 *     api/handlers/stripe/SubscriptionHandler.ts. Rows predating the rename were
 *     never migrated, so "BASIC" still appears. It is not a fourth tier, and
 *     adding it to the tier dropdown would imply it is one.
 *
 * Both are handled here so the filter and the displayed tier agree: a legacy
 * `basic` row reads as Standard and is returned when you filter by Standard.
 * Retiring these helpers is a data-migration task, not a code one.
 */

/** Canonical tiers, in ascending order. */
export const TIERS = ['standard', 'professional', 'enterprise'];

/** Superseded tier names mapped to the tier that replaced them. */
const LEGACY_TIER_ALIASES = { basic: 'standard' };

/**
 * Resolve any stored tier value to its canonical lowercase form. Unknown values
 * are returned lowercased rather than coerced, so a genuinely new tier shows up
 * as itself instead of silently reading as Standard.
 */
export function normalizeTier(raw) {
    if (typeof raw !== 'string' || !raw) return 'standard';
    const lower = raw.toLowerCase();
    return LEGACY_TIER_ALIASES[lower] || lower;
}

/**
 * Every stored spelling that should match a canonical tier — casing variants of
 * the tier itself plus of any superseded name that resolves to it. Feed this to
 * an `$in` filter so the query is casing- and history-agnostic.
 */
export function tierMatchValues(tier) {
    const canonical = normalizeTier(tier);
    const names = [canonical].concat(
        Object.keys(LEGACY_TIER_ALIASES).filter(old => LEGACY_TIER_ALIASES[old] === canonical)
    );

    const spellings = names.reduce((acc, name) => acc.concat([
        name,
        name.toUpperCase(),
        name.charAt(0).toUpperCase() + name.slice(1),
    ]), []);

    return [...new Set(spellings)];
}
